import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Badge, Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';

import { useContext, useEffect, useMemo } from 'react';
import { AppContext } from '@/shared/context/context';
import ROUTES from '../../App/Router/Routes';
import signalRMessages from '@/redux/actions/signalRMessages';
import { topbar } from '@/shared/utils/constants';
import { trpc } from '@/config/trpc';
import useToggle from '@/shared/hooks/UseToggle';
import useViewport from '@/shared/hooks/useViewport';

import { InlineSpinner, TaskIcon } from '@/shared/icons/Icons';
import TaskMenuItem from './TaskMenuItem';

const TopBarTasks = () => {
  const { socket, currentTeamUser } = useContext(AppContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { contentHeight } = useViewport(500);

  const fetchTasksToggle = useToggle();

  const { data: tasksDueAmount, refetch: refetchTaskDueAmount } = trpc.task.taskDueAmount.useQuery(
    undefined,
    {
      refetchOnWindowFocus: true,
    },
  );

  const trpcUtils = trpc.useUtils();

  const { data: tasks, isLoading: tasksIsLoading } = trpc.task.topDueTasks.useQuery(undefined, {
    enabled: fetchTasksToggle.value,
  });

  const debouncedRefetch = useMemo(
    () =>
      debounce(() => {
        refetchTaskDueAmount();
        // invalidate skips the disabled query while the dropdown is closed, it then refetches on open
        trpcUtils.task.topDueTasks.invalidate();
      }, topbar.TASKS_DEBOUNCE_TIME_OUT),
    [refetchTaskDueAmount, trpcUtils],
  );

  useEffect(() => {
    const handleTaskMessage = (...args: string[]) => {
      if (
        !currentTeamUser ||
        !args.map((arg) => arg.toLowerCase()).includes(currentTeamUser.id.toLowerCase()) ||
        document.visibilityState === 'hidden'
      ) {
        return;
      }
      debouncedRefetch();
    };

    socket?.on(signalRMessages.TaskOverviewUpdated, handleTaskMessage);

    return () => {
      socket?.off(signalRMessages.TaskOverviewUpdated, handleTaskMessage);
      debouncedRefetch.cancel();
    };
  }, [currentTeamUser, debouncedRefetch, socket]);

  return (
    <Dropdown open={fetchTasksToggle.value} onOpenChange={(_, open) => fetchTasksToggle.setValue(open)}>
      <Badge badgeContent={tasksDueAmount} badgeInset={2} max={500}>
        <MenuButton
          className="rounded-full"
          slotProps={{ root: { variant: 'plain' } }}
          slots={{ root: IconButton }}
        >
          <TaskIcon />
        </MenuButton>
      </Badge>

      <Menu className="w-80 overflow-y-auto" placement="bottom-end" style={{ maxHeight: contentHeight }}>
        {tasksIsLoading && <InlineSpinner />}
        {tasks?.map((task) => (
          <TaskMenuItem key={task.id} task={task} />
        ))}

        <MenuItem
          className="flex justify-center pt-2 text-xs uppercase text-red-500"
          onClick={() => {
            navigate(ROUTES.TASKS_OVERVIEW);
          }}
        >
          {t('showAll')}
        </MenuItem>
      </Menu>
    </Dropdown>
  );
};

export default TopBarTasks;
